import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Chip, CircularProgress, Skeleton, Typography } from "@mui/material";
import { CalendarCheck, Coffee, Timer, TrendingUp } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";
import apiEndPointsConfig from "../../../services/apiEndPoints";
import { useAttendanceEvents } from "../../../services/attendanceSocket";
import { breakLabel, formatDuration, formatTime, isoDate } from "../../../utils/attendance";
import { LOCATION_DENIED, captureLocation } from "../../../utils/geolocation";
import { captureDevice } from "../../../utils/device";
import { AGENT_INK, AGENT_LINE, AGENT_MUTED } from "../../../agentComponents/agentTheme";
import ClockCard from "./ClockCard";
import DayTimeline from "./DayTimeline";
import WeeklyHours from "./WeeklyHours";
import StatTiles from "./StatTiles";

// The agent's attendance home. One open shift at a time; breaks nest inside it.
//
// Elapsed time is computed from the server's timestamps against a server/client
// clock offset measured at load, so an agent whose laptop clock is wrong still
// sees the hours the server will actually credit them with.

const TICK_MS = 1000;
const TARGET_HOURS = 8;

export default function AttendancePanel({ onChanged }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [current, setCurrent] = useState(null);
  const [today, setToday] = useState(null);
  const [agent, setAgent] = useState(null);
  const [week, setWeek] = useState([]);
  const [breakTypes, setBreakTypes] = useState([]);
  const [breakType, setBreakType] = useState("");
  const [breakNote, setBreakNote] = useState("");
  const [locationNote, setLocationNote] = useState("");

  const offsetRef = useRef(0);
  const [tick, setTick] = useState(0);
  const now = () => Date.now() + offsetRef.current;

  const load = useCallback(async () => {
    try {
      const requestedAt = Date.now();
      const [me, history] = await Promise.all([
        axiosInstance.get(apiEndPointsConfig.attendanceMe),
        axiosInstance.get(apiEndPointsConfig.attendanceMyHistory, {
          params: { from: isoDate(6), to: isoDate(0), limit: 100 },
        }),
      ]);

      const data = me.data;
      if (data?.serverTime) {
        // Charge half the round trip to the response leg.
        const latency = (Date.now() - requestedAt) / 2;
        offsetRef.current = new Date(data.serverTime).getTime() + latency - Date.now();
      }
      setCurrent(data?.current || null);
      setToday(data?.today || null);
      setAgent(data?.agent || null);
      setWeek(history.data?.data || []);

      const types = Array.isArray(data?.breakTypes) ? data.breakTypes : [];
      setBreakTypes(types);
      // Keep the current pick if it survived an admin edit, else fall back to
      // the first available type so the picker is never empty-but-enabled.
      setBreakType((prev) => (types.some((t) => t.key === prev) ? prev : types[0]?.key || ""));
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load your attendance");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // A second tab (or an admin force clock-out) changes state under us.
  useAttendanceEvents("attendance.self", () => load(), load);

  const state = current ? current.status : "off";

  // Only tick while something is actually running.
  useEffect(() => {
    if (state === "off") return undefined;
    const id = setInterval(() => setTick((n) => n + 1), TICK_MS);
    return () => clearInterval(id);
  }, [state]);

  /** Returns true only when the server accepted the action. */
  const act = async (key, url, body) => {
    setBusy(key);
    setError("");
    setNotice("");
    try {
      const { data } = await axiosInstance.post(url, body || {});
      setNotice(data?.message || "Done");
      await load();
      if (onChanged) onChanged();
      return true;
    } catch (err) {
      setError(err?.response?.data?.message || "That did not go through. Try again.");
      // A 409 means our view of the shift is stale -- resync rather than guess.
      if (err?.response?.status === 409) load();
      return false;
    } finally {
      setBusy("");
    }
  };

  /**
   * Clock in/out carry the agent's location when the browser will give it,
   * plus whatever device details it exposes (OS, browser, phone model).
   * The capture is awaited first but can never fail the punch -- it resolves to
   * null on refusal or timeout and the request goes out regardless.
   */
  const punch = async (key, url) => {
    setBusy(key);
    const [{ coords, reason }, device] = await Promise.all([captureLocation(), captureDevice()]);
    setBusy("");

    setLocationNote(
      coords
        ? ""
        : reason === LOCATION_DENIED
        ? "Location not shared — your punch was still recorded."
        : "Location unavailable — your punch was still recorded."
    );

    return act(key, url, { ...(coords ? { location: coords } : {}), ...(device ? { device } : {}) });
  };

  const startBreak = async () => {
    const accepted = await act("breakStart", apiEndPointsConfig.attendanceBreakStart, {
      type: breakType,
      note: breakNote,
    });
    // The note describes the break that just started and is already stored on
    // it. Leaving it in the box would silently attach it to the next break too.
    // Only cleared on success, so a failed attempt does not lose what was typed.
    if (accepted) setBreakNote("");
  };

  const live = useMemo(() => {
    if (!current) return { shiftMs: 0, breakMs: 0, productiveMs: 0, currentBreakMs: 0 };
    const t = now();
    const end = current.clockOutAt ? new Date(current.clockOutAt).getTime() : t;
    const shiftMs = Math.max(0, end - new Date(current.clockInAt).getTime());
    let breakMs = 0;
    for (const b of current.breaks || []) {
      const bEnd = b.endAt ? new Date(b.endAt).getTime() : end;
      breakMs += Math.max(0, bEnd - new Date(b.startAt).getTime());
    }
    breakMs = Math.min(breakMs, shiftMs);
    const currentBreakMs = current.currentBreak
      ? Math.max(0, t - new Date(current.currentBreak.startAt).getTime())
      : 0;
    return { shiftMs, breakMs, productiveMs: Math.max(0, shiftMs - breakMs), currentBreakMs };
    // tick is the clock pulse: it is what re-runs this each second.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, tick]);

  // Today's totals, including the shift still running.
  const todayTotals = useMemo(() => {
    const base = { workedMs: 0, breakMs: 0, productiveMs: 0 };
    for (const s of today?.shifts || []) {
      if (current && s.id === current.id) continue; // counted live below
      base.workedMs += s.totals.workedMs;
      base.breakMs += s.totals.breakMs;
      base.productiveMs += s.totals.productiveMs;
    }
    if (current) {
      base.workedMs += live.shiftMs;
      base.breakMs += live.breakMs;
      base.productiveMs += live.productiveMs;
    }
    return base;
  }, [today, current, live]);

  // Week-to-date averages. Only days with a shift count towards the average --
  // dividing by seven would punish an agent for their days off.
  const weekStats = useMemo(() => {
    const byDay = new Map();
    for (const s of week) {
      const acc = byDay.get(s.date) || { productiveMs: 0, breakMs: 0, late: false };
      acc.productiveMs += s.totals.productiveMs;
      acc.breakMs += s.totals.breakMs;
      if (s.lateBy > 0) acc.late = true;
      byDay.set(s.date, acc);
    }
    const days = [...byDay.values()];
    const present = days.length;
    const totalProductive = days.reduce((n, d) => n + d.productiveMs, 0);
    const totalBreak = days.reduce((n, d) => n + d.breakMs, 0);
    const onTime = days.filter((d) => !d.late).length;
    return {
      present,
      avgProductiveMs: present ? totalProductive / present : 0,
      avgBreakMs: present ? totalBreak / present : 0,
      onTimePct: present ? Math.round((onTime / present) * 100) : null,
    };
  }, [week]);

  if (loading) {
    // Mirrors the real layout below so nothing jumps when the data lands.
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: "12px" }} />
          ))}
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 360px) minmax(0, 1fr)" },
            gap: 2.5,
          }}
        >
          <Skeleton variant="rounded" height={470} sx={{ borderRadius: "16px" }} />
          <Skeleton variant="rounded" height={470} sx={{ borderRadius: "16px" }} />
        </Box>
      </Box>
    );
  }

  const todayShifts = today?.shifts || [];
  const progressPct = Math.min(100, Math.round((todayTotals.productiveMs / (TARGET_HOURS * 3600000)) * 100));

  const tiles = [
    {
      label: "Effective Today",
      value: formatDuration(todayTotals.productiveMs),
      hint: `${progressPct}% of ${TARGET_HOURS}h target`,
      hintColor: progressPct >= 100 ? "#16A34A" : undefined,
      icon: <Timer size={17} />,
      color: "#16A34A",
      bg: "#ECFDF5",
    },
    {
      label: "Break Today",
      value: formatDuration(todayTotals.breakMs),
      hint: `${current?.breaks?.length || 0} break(s) this shift`,
      icon: <Coffee size={17} />,
      color: "#D97706",
      bg: "#FFFBEB",
    },
    {
      label: "Avg / Day",
      value: formatDuration(weekStats.avgProductiveMs),
      hint: `across ${weekStats.present} day(s) worked`,
      icon: <TrendingUp size={17} />,
      color: "#0C237B",
      bg: "#EEF2FF",
    },
    {
      label: "On-Time Arrival",
      value: weekStats.onTimePct === null ? "--" : `${weekStats.onTimePct}%`,
      hint: agent?.weekOff
        ? "week off today"
        : agent?.shiftStart
        ? `shift starts ${agent.shiftStart}`
        : "no shift set",
      hintColor: weekStats.onTimePct !== null && weekStats.onTimePct < 80 ? "#DC2626" : undefined,
      icon: <CalendarCheck size={17} />,
      color: "#7C3AED",
      bg: "#F5F3FF",
    },
  ];

  return (
    // CSS grid throughout: a MUI Grid container with spacing carries negative
    // margins that collapse against siblings, which is what made the clock card
    // ride up over the stat tiles.
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {error && (
        <Alert severity="error" sx={{ borderRadius: "12px" }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ borderRadius: "12px" }} onClose={() => setNotice("")}>
          {notice}
        </Alert>
      )}
      {locationNote && (
        <Alert severity="info" sx={{ borderRadius: "12px" }} onClose={() => setLocationNote("")}>
          {locationNote}
        </Alert>
      )}

      <StatTiles tiles={tiles} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 360px) minmax(0, 1fr)" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <ClockCard
            state={state}
            current={current}
            live={live}
            agent={agent}
            breakTypes={breakTypes}
            breakType={breakType}
            setBreakType={setBreakType}
            breakNote={breakNote}
            setBreakNote={setBreakNote}
            busy={busy}
            onClockIn={() => punch("in", apiEndPointsConfig.attendanceClockIn)}
            onClockOut={() => punch("out", apiEndPointsConfig.attendanceClockOut)}
            onBreakStart={startBreak}
            onBreakEnd={() => act("breakEnd", apiEndPointsConfig.attendanceBreakEnd)}
          />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, minWidth: 0 }}>
          <DayTimeline shifts={todayShifts} agent={agent} now={now()} />
          <WeeklyHours shifts={week} targetHours={TARGET_HOURS} />
        </Box>
      </Box>

      {/* ── Breaks in the running shift ────────────────────────────────── */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${AGENT_LINE}`, p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: AGENT_INK }}>
                Breaks This Shift
              </Typography>
              {current?.breaks?.length > 0 && (
                <Chip
                  size="small"
                  label={formatDuration(live.breakMs)}
                  sx={{ bgcolor: "#FFFBEB", color: "#B45309", fontWeight: 700, fontSize: 11.5 }}
                />
              )}
            </Box>

            {!current?.breaks?.length ? (
              <Typography sx={{ fontSize: 13, color: AGENT_MUTED, py: 2 }}>
                {current ? "No breaks taken yet this shift." : "Clock in to start tracking breaks."}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {current.breaks.map((b) => {
                  const running = !b.endAt;
                  const duration = running
                    ? Math.max(0, now() - new Date(b.startAt).getTime())
                    : b.durationMs;
                  return (
                    <Box
                      key={b.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderRadius: "10px",
                        bgcolor: running ? "#FFFBEB" : "#F8FAFC",
                        border: `1px solid ${running ? "#FDE68A" : AGENT_LINE}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: b.overran ? "#DC2626" : running ? "#F59E0B" : "#CBD5E1",
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: AGENT_INK }}>
                            {breakLabel(b)}
                          </Typography>
                          {running && (
                            <Chip
                              label="running"
                              size="small"
                              sx={{ height: 17, fontSize: 10, bgcolor: "#FEF3C7", color: "#B45309", fontWeight: 700 }}
                            />
                          )}
                          {b.overran && (
                            <Chip
                              label={`over ${b.maxMinutes}m`}
                              size="small"
                              sx={{ height: 17, fontSize: 10, bgcolor: "#FEF2F2", color: "#DC2626", fontWeight: 700 }}
                            />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: 11.5, color: AGENT_MUTED }} noWrap>
                          {formatTime(b.startAt)} - {b.endAt ? formatTime(b.endAt) : "now"}
                          {b.note ? ` · ${b.note}` : ""}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13.5,
                          fontWeight: 700,
                          color: b.overran ? "#DC2626" : AGENT_MUTED,
                          fontFamily: "ui-monospace, Menlo, monospace",
                        }}
                      >
                        {formatDuration(duration)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
      </Box>

      {busy && (
        <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}>
          <CircularProgress size={28} />
        </Box>
      )}
    </Box>
  );
}
